import React, { useEffect, useMemo, useState } from "react";
import {
  FaChartBar,
  FaRegChartBar,
  FaShoppingCart,
  FaUser,
} from "react-icons/fa";
import { BiCategory } from "react-icons/bi";
import {
  IoChevronDown,
  IoChevronForward,
  IoClose,
  IoMenu,
  IoSparkles,
} from "react-icons/io5";
import { MdOutlinePermMedia } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

const NAVBAR_HEIGHT = 64;
const MOBILE_TOP_OFFSET = -5.4;

const COLORS = {
  maroon: "#2B0000",
  maroonDeep: "#170000",
  maroonSoft: "#4A090B",
  gold: "#D4AF37",
  goldSoft: "#E9CD7A",
  cream: "#FFF8EF",
  creamSoft: "#F5EBDD",
  muted: "#B9A79A",
};

const menuSections = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: FaChartBar,
    parentPath: "dashboard",
    activePages: ["dashboard"],
    children: [{ label: "Overview", path: "dashboard" }],
  },
  {
    key: "ecommerce",
    label: "Ecommerce",
    icon: FaShoppingCart,
    activePages: [
      "add-product",
      "product-list",
      "manage-discount",
      "update-product",
      "stock-management",
    ],
    children: [
      { label: "Add Product", path: "add-product" },
      { label: "Product List", path: "product-list" },
      { label: "Update Product", path: "update-product" },
      { label: "Stock Management", path: "stock-management" },
    ],
  },
  {
    key: "reporting",
    label: "Reporting",
    icon: FaRegChartBar,
    activePages: [
      "detailed-report",
      "order-summary",
      "product-report",
      "recent-orders",
    ],
    children: [
      { label: "Order Detailed Report", path: "detailed-report" },
      { label: "Order Summary", path: "order-summary" },
      { label: "Product Report", path: "product-report" },
      { label: "Recent Orders", path: "recent-orders" },
    ],
  },
  {
    key: "categories",
    label: "Categories",
    icon: BiCategory,
    activePages: [
      "add-category",
      "category-list",
      "add-product-size",
      // "hsn-code",
      "purities",
    ],
    children: [
      { label: "Add Category", path: "add-category" },
      { label: "Category List", path: "category-list" },
      { label: "Add Category Size", path: "add-product-size" },
      // { label: "Enter HSN Code", path: "hsn-code" },
      { label: "Product Purities", path: "purities" },
    ],
  },
  {
    key: "banner",
    label: "Dynamic Sections",
    icon: MdOutlinePermMedia,
    prefix: "banner",
    children: [
      { label: "Banner", path: "banner" },
      { label: "Home Hero", path: "banner/home-hero" },
      { label: "Why Kanthi", path: "banner/why-kanthi" },
      { label: "Blogs", path: "banner/blogs" },
      { label: "About Us Page", path: "banner/about-us" },
      { label: "Teams", path: "banner/teams" },
      { label: "Awards", path: "banner/awards" },
      { label: "Trade Fairs", path: "banner/trade-fairs" },
      { label: "Contact Messages", path: "banner/contact-messages" },
      // { label: "Best Sellers", path: "banner/marquee" },
      { label: "Coupon", path: "banner/coupon" },
    ],
  },
  {
    key: "user",
    label: "User",
    icon: FaUser,
    activePages: ["user-list"],
    children: [{ label: "All Users", path: "user-list" }],
  },
];

const getPageFromPath = (pathname) => {
  if (!pathname.startsWith("/admin/")) return "";
  return pathname.replace("/admin/", "").replace(/^\/+|\/+$/g, "");
};

const Sidebar = ({ isOpen, toggleSidebar, isDesktop }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [localIsDesktop, setLocalIsDesktop] = useState(isDesktop);
  const [openDropdown, setOpenDropdown] = useState(null);

  const activePage = useMemo(
    () => getPageFromPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    setLocalIsDesktop(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    const handleResize = () => {
      setLocalIsDesktop(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const matchingSection = menuSections.find((section) => {
      if (section.prefix) {
        return (
          activePage === section.prefix ||
          activePage.startsWith(`${section.prefix}/`)
        );
      }

      return section.activePages?.includes(activePage);
    });

    if (matchingSection) {
      setOpenDropdown(matchingSection.key);
    }
  }, [activePage]);

  useEffect(() => {
    if (!localIsDesktop) {
      document.body.style.overflow = isOpen ? "hidden" : "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, localIsDesktop]);

  const topPx = localIsDesktop
    ? NAVBAR_HEIGHT
    : Math.max(0, NAVBAR_HEIGHT + MOBILE_TOP_OFFSET);

  const isSectionActive = (section) => {
    if (section.prefix) {
      return (
        activePage === section.prefix ||
        activePage.startsWith(`${section.prefix}/`)
      );
    }

    return section.activePages?.includes(activePage);
  };

  const handleNavigation = (path) => {
    navigate(`/admin/${path}`);

    if (!localIsDesktop) {
      toggleSidebar(false);
    }
  };

  const handleSectionClick = (section) => {
    if (section.parentPath) {
      handleNavigation(section.parentPath);
    }

    setOpenDropdown((current) =>
      current === section.key ? null : section.key
    );
  };

  return (
    <>
      {!localIsDesktop && (
        <button
          type="button"
          onClick={() => toggleSidebar(true)}
          aria-label="Open admin navigation"
          className={`admin-sidebar-launcher ${
            isOpen ? "admin-sidebar-launcher-hidden" : ""
          }`}
          style={{ top: `calc(50% + ${NAVBAR_HEIGHT / 2}px)` }}
        >
          <IoMenu />
        </button>
      )}

      {!localIsDesktop && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={() => toggleSidebar(false)}
          className={`admin-sidebar-overlay ${
            isOpen ? "admin-sidebar-overlay-visible" : ""
          }`}
          style={{ top: topPx }}
        />
      )}

      <aside
        className={`admin-sidebar ${
          localIsDesktop || isOpen
            ? "admin-sidebar-visible"
            : "admin-sidebar-hidden"
        }`}
        style={{
          top: topPx,
          height: `calc(100vh - ${topPx}px)`,
        }}
      >
        <div className="admin-sidebar-shine" />
        <div className="admin-sidebar-orb admin-sidebar-orb-one" />
        <div className="admin-sidebar-orb admin-sidebar-orb-two" />

        <header className="admin-sidebar-header">
          <div className="admin-brand-mark">
            <span className="admin-brand-ring">
              <IoSparkles />
            </span>

            <div className="admin-brand-copy">
              <p>SL GOLD</p>
              <span>Luxury Admin</span>
            </div>
          </div>

          {!localIsDesktop && (
            <button
              type="button"
              onClick={() => toggleSidebar(false)}
              className="admin-sidebar-close"
              aria-label="Close sidebar"
            >
              <IoClose />
            </button>
          )}
        </header>

        <div className="admin-sidebar-rule">
          <span />
          <i />
          <span />
        </div>

        <div className="admin-sidebar-caption">
          <span>Navigation</span>
          <small>Management Console</small>
        </div>

        <nav className="admin-sidebar-nav">
          {menuSections.map((section) => {
            const Icon = section.icon;
            const active = isSectionActive(section);
            const expanded = openDropdown === section.key;

            return (
              <div
                key={section.key}
                className={`admin-menu-section ${
                  active ? "admin-menu-section-active" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleSectionClick(section)}
                  className={`admin-menu-parent ${
                    active ? "admin-menu-parent-active" : ""
                  }`}
                >
                  <span className="admin-menu-icon">
                    <Icon />
                  </span>

                  <span className="admin-menu-label">{section.label}</span>

                  <span
                    className={`admin-menu-arrow ${
                      expanded ? "admin-menu-arrow-open" : ""
                    }`}
                  >
                    <IoChevronDown />
                  </span>
                </button>

                <div
                  className={`admin-submenu ${
                    expanded ? "admin-submenu-open" : ""
                  }`}
                >
                  <div className="admin-submenu-inner">
                    {section.children.map((item) => {
                      const childActive = activePage === item.path;

                      return (
                        <button
                          type="button"
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className={`admin-submenu-item ${
                            childActive ? "admin-submenu-item-active" : ""
                          }`}
                        >
                          <span className="admin-submenu-dot" />
                          <span>{item.label}</span>
                          <IoChevronForward className="admin-submenu-chevron" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <footer className="admin-sidebar-footer">
          <div className="admin-footer-jewel">
            <IoSparkles />
          </div>

          <div>
            <p>S. L. GOLD</p>
            <span>Admin Panel · v1.0.0</span>
          </div>
        </footer>
      </aside>

      <style>{`
        .admin-sidebar,
        .admin-sidebar *,
        .admin-sidebar-launcher,
        .admin-sidebar-overlay {
          box-sizing: border-box;
        }

        .admin-sidebar {
          position: fixed;
          left: 0;
          z-index: 50;
          display: flex;
          width: 286px;
          min-height: 0;
          flex-direction: column;
          overflow: hidden;
          color: ${COLORS.cream};
          background:
            radial-gradient(
              circle at 15% 4%,
              rgba(130, 20, 26, 0.42),
              transparent 30%
            ),
            radial-gradient(
              circle at 90% 75%,
              rgba(105, 8, 13, 0.28),
              transparent 35%
            ),
            linear-gradient(
              160deg,
              ${COLORS.maroonSoft} 0%,
              ${COLORS.maroon} 38%,
              ${COLORS.maroonDeep} 100%
            );
          border-right: 1px solid rgba(212, 175, 55, 0.34);
          box-shadow:
            18px 0 55px rgba(23, 0, 0, 0.2),
            inset -1px 0 0 rgba(255, 255, 255, 0.035);
          transform: translateX(0);
          transition:
            transform 420ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 260ms ease;
          font-family: "Kanit", Arial, sans-serif;
          isolation: isolate;
        }

        .admin-sidebar::before {
          content: "";
          position: absolute;
          inset: 10px;
          z-index: -1;
          border: 1px solid rgba(212, 175, 55, 0.1);
          border-radius: 18px;
          pointer-events: none;
        }

        .admin-sidebar-visible {
          opacity: 1;
          transform: translateX(0);
        }

        .admin-sidebar-hidden {
          opacity: 0;
          transform: translateX(-105%);
          pointer-events: none;
        }

        .admin-sidebar-shine {
          position: absolute;
          top: -35%;
          left: -80%;
          z-index: -1;
          width: 90%;
          height: 180%;
          opacity: 0.09;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 236, 176, 0.8),
            transparent
          );
          transform: rotate(20deg);
          pointer-events: none;
        }

        .admin-sidebar-orb {
          position: absolute;
          z-index: -2;
          border-radius: 50%;
          filter: blur(65px);
          pointer-events: none;
        }

        .admin-sidebar-orb-one {
          top: -85px;
          left: -45px;
          width: 210px;
          height: 210px;
          background: rgba(126, 24, 31, 0.35);
        }

        .admin-sidebar-orb-two {
          right: -80px;
          bottom: 80px;
          width: 190px;
          height: 190px;
          background: rgba(171, 110, 22, 0.12);
        }

        .admin-sidebar-header {
          position: relative;
          z-index: 2;
          display: flex;
          min-height: 84px;
          align-items: center;
          justify-content: space-between;
          padding: 17px 18px 12px;
          flex-shrink: 0;
        }

        .admin-brand-mark {
          display: flex;
          min-width: 0;
          align-items: center;
          gap: 12px;
        }

        .admin-brand-ring {
          display: grid;
          width: 44px;
          height: 44px;
          flex: 0 0 auto;
          place-items: center;
          border: 1px solid rgba(233, 205, 122, 0.7);
          border-radius: 50%;
          color: ${COLORS.goldSoft};
          background:
            radial-gradient(
              circle at 35% 25%,
              rgba(255, 238, 177, 0.18),
              transparent 45%
            ),
            rgba(23, 0, 0, 0.55);
          box-shadow:
            0 10px 25px rgba(0, 0, 0, 0.22),
            inset 0 0 0 4px rgba(212, 175, 55, 0.05);
        }

        .admin-brand-ring svg {
          width: 21px;
          height: 21px;
        }

        .admin-brand-copy {
          min-width: 0;
        }

        .admin-brand-copy p {
          margin: 0;
          overflow: hidden;
          color: ${COLORS.goldSoft};
          font-family: Georgia, "Times New Roman", serif;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.1;
          letter-spacing: 0.13em;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .admin-brand-copy span {
          display: block;
          margin-top: 5px;
          color: rgba(255, 248, 239, 0.58);
          font-size: 10px;
          line-height: 1;
          letter-spacing: 0.2em;
          text-transform: uppercase;
        }

        .admin-sidebar-close {
          display: grid;
          width: 36px;
          height: 36px;
          flex: 0 0 auto;
          place-items: center;
          border: 1px solid rgba(233, 205, 122, 0.3);
          border-radius: 9px;
          color: ${COLORS.goldSoft};
          background: rgba(23, 0, 0, 0.4);
          cursor: pointer;
          transition:
            color 220ms ease,
            background 220ms ease,
            transform 220ms ease;
        }

        .admin-sidebar-close:hover {
          color: ${COLORS.maroonDeep};
          background: ${COLORS.goldSoft};
          transform: rotate(4deg);
        }

        .admin-sidebar-close svg {
          width: 22px;
          height: 22px;
        }

        .admin-sidebar-rule {
          display: flex;
          align-items: center;
          gap: 9px;
          margin: 0 18px;
          color: rgba(212, 175, 55, 0.55);
        }

        .admin-sidebar-rule span {
          flex: 1;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            currentColor
          );
        }

        .admin-sidebar-rule span:last-child {
          background: linear-gradient(
            90deg,
            currentColor,
            transparent
          );
        }

        .admin-sidebar-rule i {
          display: block;
          width: 7px;
          height: 7px;
          border: 1px solid currentColor;
          transform: rotate(45deg);
        }

        .admin-sidebar-caption {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 20px 8px;
        }

        .admin-sidebar-caption span {
          color: rgba(255, 248, 239, 0.88);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .admin-sidebar-caption small {
          color: rgba(233, 205, 122, 0.46);
          font-size: 8px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .admin-sidebar-nav {
          position: relative;
          z-index: 2;
          flex: 1;
          min-height: 0;
          overflow-x: hidden;
          overflow-y: auto;
          padding: 5px 12px 22px;
          scrollbar-color: rgba(212, 175, 55, 0.35) transparent;
          scrollbar-width: thin;
        }

        .admin-sidebar-nav::-webkit-scrollbar {
          width: 5px;
        }

        .admin-sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .admin-sidebar-nav::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(212, 175, 55, 0.28);
        }

        .admin-menu-section {
          position: relative;
          margin-bottom: 7px;
          border: 1px solid transparent;
          border-radius: 13px;
          transition:
            border-color 250ms ease,
            background 250ms ease,
            box-shadow 250ms ease;
        }

        .admin-menu-section-active {
          border-color: rgba(212, 175, 55, 0.18);
          background:
            linear-gradient(
              110deg,
              rgba(212, 175, 55, 0.09),
              rgba(255, 255, 255, 0.025)
            );
          box-shadow: inset 3px 0 0 rgba(212, 175, 55, 0.72);
        }

        .admin-menu-parent {
          display: flex;
          width: 100%;
          min-height: 51px;
          align-items: center;
          gap: 12px;
          padding: 9px 12px;
          border: 0;
          border-radius: 12px;
          color: rgba(255, 248, 239, 0.74);
          background: transparent;
          font-family: inherit;
          text-align: left;
          cursor: pointer;
          transition:
            color 220ms ease,
            background 220ms ease,
            transform 220ms ease;
        }

        .admin-menu-parent:hover {
          color: ${COLORS.cream};
          background: rgba(255, 255, 255, 0.045);
          transform: translateX(2px);
        }

        .admin-menu-parent-active {
          color: ${COLORS.goldSoft};
        }

        .admin-menu-icon {
          display: grid;
          width: 34px;
          height: 34px;
          flex: 0 0 auto;
          place-items: center;
          border: 1px solid rgba(212, 175, 55, 0.15);
          border-radius: 9px;
          color: rgba(233, 205, 122, 0.82);
          background: rgba(23, 0, 0, 0.32);
          transition:
            border-color 220ms ease,
            color 220ms ease,
            background 220ms ease;
        }

        .admin-menu-parent:hover .admin-menu-icon,
        .admin-menu-parent-active .admin-menu-icon {
          border-color: rgba(233, 205, 122, 0.42);
          color: ${COLORS.goldSoft};
          background: rgba(212, 175, 55, 0.09);
        }

        .admin-menu-icon svg {
          width: 16px;
          height: 16px;
        }

        .admin-menu-label {
          min-width: 0;
          flex: 1;
          overflow: hidden;
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.025em;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .admin-menu-arrow {
          display: grid;
          width: 24px;
          height: 24px;
          flex: 0 0 auto;
          place-items: center;
          color: rgba(233, 205, 122, 0.55);
          transition: transform 280ms ease;
        }

        .admin-menu-arrow-open {
          transform: rotate(180deg);
        }

        .admin-menu-arrow svg {
          width: 14px;
          height: 14px;
        }

        .admin-submenu {
          display: grid;
          grid-template-rows: 0fr;
          opacity: 0;
          transition:
            grid-template-rows 350ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 220ms ease;
        }

        .admin-submenu-open {
          grid-template-rows: 1fr;
          opacity: 1;
        }

        .admin-submenu-inner {
          position: relative;
          min-height: 0;
          overflow: hidden;
          padding: 0 8px 0 50px;
        }

        .admin-submenu-open .admin-submenu-inner {
          padding-bottom: 9px;
        }

        .admin-submenu-inner::before {
          content: "";
          position: absolute;
          top: 2px;
          bottom: 11px;
          left: 32px;
          width: 1px;
          background: linear-gradient(
            180deg,
            rgba(212, 175, 55, 0.6),
            rgba(212, 175, 55, 0.06)
          );
        }

        .admin-submenu-item {
          position: relative;
          display: flex;
          width: 100%;
          min-height: 36px;
          align-items: center;
          gap: 9px;
          margin-top: 2px;
          padding: 7px 8px;
          border: 0;
          border-radius: 8px;
          color: rgba(255, 248, 239, 0.56);
          background: transparent;
          font-family: inherit;
          font-size: 11px;
          line-height: 1.25;
          text-align: left;
          cursor: pointer;
          transition:
            color 200ms ease,
            background 200ms ease,
            padding-left 200ms ease;
        }

        .admin-submenu-item:hover {
          color: rgba(255, 248, 239, 0.92);
          background: rgba(255, 255, 255, 0.04);
          padding-left: 11px;
        }

        .admin-submenu-item-active {
          color: ${COLORS.goldSoft};
          background: rgba(212, 175, 55, 0.1);
        }

        .admin-submenu-dot {
          width: 5px;
          height: 5px;
          flex: 0 0 auto;
          border: 1px solid currentColor;
          border-radius: 50%;
          background: transparent;
        }

        .admin-submenu-item-active .admin-submenu-dot {
          border-color: ${COLORS.gold};
          background: ${COLORS.gold};
          box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.08);
        }

        .admin-submenu-chevron {
          width: 12px;
          height: 12px;
          margin-left: auto;
          opacity: 0;
          transform: translateX(-4px);
          transition:
            opacity 180ms ease,
            transform 180ms ease;
        }

        .admin-submenu-item:hover .admin-submenu-chevron,
        .admin-submenu-item-active .admin-submenu-chevron {
          opacity: 1;
          transform: translateX(0);
        }

        .admin-sidebar-footer {
          position: relative;
          z-index: 2;
          display: flex;
          min-height: 72px;
          flex-shrink: 0;
          align-items: center;
          gap: 12px;
          padding: 13px 18px;
          border-top: 1px solid rgba(212, 175, 55, 0.13);
          background:
            linear-gradient(
              90deg,
              rgba(23, 0, 0, 0.5),
              rgba(74, 9, 11, 0.38)
            );
        }

        .admin-footer-jewel {
          display: grid;
          width: 35px;
          height: 35px;
          flex: 0 0 auto;
          place-items: center;
          border: 1px solid rgba(212, 175, 55, 0.28);
          border-radius: 50%;
          color: ${COLORS.goldSoft};
          background: rgba(23, 0, 0, 0.4);
        }

        .admin-footer-jewel svg {
          width: 16px;
          height: 16px;
        }

        .admin-sidebar-footer p {
          margin: 0;
          color: rgba(255, 248, 239, 0.8);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 12px;
          letter-spacing: 0.1em;
        }

        .admin-sidebar-footer span {
          display: block;
          margin-top: 4px;
          color: rgba(255, 248, 239, 0.38);
          font-size: 9px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .admin-sidebar-overlay {
          position: fixed;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 40;
          border: 0;
          opacity: 0;
          background:
            radial-gradient(
              circle at left center,
              rgba(74, 9, 11, 0.28),
              transparent 52%
            ),
            rgba(15, 0, 0, 0.56);
          backdrop-filter: blur(4px);
          pointer-events: none;
          transition: opacity 280ms ease;
        }

        .admin-sidebar-overlay-visible {
          opacity: 1;
          pointer-events: auto;
        }

        .admin-sidebar-launcher {
          position: fixed;
          left: 0;
          z-index: 45;
          display: grid;
          width: 42px;
          height: 54px;
          place-items: center;
          border: 1px solid rgba(212, 175, 55, 0.52);
          border-left: 0;
          border-radius: 0 14px 14px 0;
          color: ${COLORS.goldSoft};
          background:
            radial-gradient(
              circle at 25% 20%,
              rgba(121, 17, 23, 0.72),
              transparent 48%
            ),
            ${COLORS.maroon};
          box-shadow: 8px 10px 28px rgba(43, 0, 0, 0.25);
          cursor: pointer;
          transform: translateY(-50%);
          transition:
            opacity 220ms ease,
            transform 220ms ease,
            width 220ms ease;
        }

        .admin-sidebar-launcher:hover {
          width: 48px;
          transform: translateY(-50%) translateX(2px);
        }

        .admin-sidebar-launcher svg {
          width: 21px;
          height: 21px;
        }

        .admin-sidebar-launcher-hidden {
          opacity: 0;
          pointer-events: none;
          transform: translate(-100%, -50%);
        }

        @media (min-width: 1024px) {
          .admin-sidebar {
            opacity: 1 !important;
            transform: translateX(0) !important;
            pointer-events: auto !important;
          }
        }

        @media (max-width: 1023px) {
          .admin-sidebar {
            width: min(292px, calc(100vw - 46px));
            border-radius: 0 22px 22px 0;
          }

          .admin-sidebar::before {
            border-radius: 15px;
          }
        }

        @media (max-width: 420px) {
          .admin-sidebar {
            width: min(278px, calc(100vw - 28px));
          }

          .admin-sidebar-caption small {
            display: none;
          }

          .admin-sidebar-header {
            padding-inline: 15px;
          }

          .admin-sidebar-nav {
            padding-inline: 9px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .admin-sidebar,
          .admin-menu-parent,
          .admin-menu-arrow,
          .admin-submenu,
          .admin-submenu-item,
          .admin-sidebar-launcher,
          .admin-sidebar-overlay {
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;
